/**
 * CRM contacts with phone numbers for SIP dialer (same data source as email composer CRM tab).
 */

'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {Search, Phone, Globe, Building2, ExternalLink} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CrmDialContactRow } from '../types';
import { fetchCrmContactsForDialer } from '../fetchCrmContactsForDialer';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

function primaryPhone(c: CrmDialContactRow): string | null {
  const mobile = c.mobile?.trim();
  const phone = c.phone?.trim();
  return mobile || phone || null;
}

function displayName(c: CrmDialContactRow): string {
  if (c.first_name || c.last_name) {
    return `${c.first_name || ''} ${c.last_name || ''}`.trim();
  }
  return c.name || c.email || 'Contact';
}

export interface CrmDialContactsListProps {
  isDark: boolean;
  primaryColor: string;
  onSelectPhone: (e164OrRaw: string, displayLabel: string) => void;
  onOpenContact?: (contactId: number) => void;
  /** Toolbar-driven search (omit local search field). */
  toolbarSearchQuery?: string;
  onToolbarSearchChange?: (q: string) => void;
  /** Sort filtered rows by name */
  nameSort?: 'asc' | 'desc';
}

export function CrmDialContactsList({
  isDark,
  primaryColor,
  onSelectPhone,
  onOpenContact,
  toolbarSearchQuery,
  onToolbarSearchChange,
  nameSort = 'asc',
}: CrmDialContactsListProps) {
  const [loading, setLoading] = useState(true);
  const [internalSearch, setInternalSearch] = useState('');
  const [rows, setRows] = useState<CrmDialContactRow[]>([]);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const isToolbarSearch =
    toolbarSearchQuery !== undefined && onToolbarSearchChange !== undefined;
  const search = isToolbarSearch ? toolbarSearchQuery : internalSearch;
  const setSearch = isToolbarSearch ? onToolbarSearchChange : setInternalSearch;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const deduped = await fetchCrmContactsForDialer();
      setRows(deduped);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const contactTypes = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((c) => {
      if (c.contact_type) s.add(c.contact_type);
    });
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows;
    if (typeFilter !== 'all') {
      list = list.filter((c) => c.contact_type?.toLowerCase() === typeFilter.toLowerCase());
    }
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((c) => {
      const name = displayName(c).toLowerCase();
      const phone = (primaryPhone(c) || '').toLowerCase();
      const co = (c.company || '').toLowerCase();
      const em = (c.email || '').toLowerCase();
      return name.includes(q) || phone.includes(q) || co.includes(q) || em.includes(q);
    });
  }, [rows, search, typeFilter]);

  const filteredSorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const cmp = displayName(a).localeCompare(displayName(b), undefined, { sensitivity: 'base' });
      return nameSort === 'desc' ? -cmp : cmp;
    });
    return arr;
  }, [filtered, nameSort]);

  return (
    <div className="space-y-3 p-3 sm:p-4">
      <div className="flex items-center gap-2 rounded-lg border border-blue-200/60 bg-blue-50/80 px-3 py-2 dark:border-blue-900/50 dark:bg-blue-950/40">
        <Globe className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
        <p className="text-xs text-blue-900 dark:text-blue-200">
          Customers & suppliers from CRM. Tap call to enter the number on the dial pad.
        </p>
      </div>

      <div
        className={cn(
          'flex flex-col gap-2 sm:flex-row sm:items-center',
          isToolbarSearch && 'sm:justify-end',
        )}
      >
        {!isToolbarSearch ? (
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search CRM name, company, number..."
              className={cn(
                'w-full rounded-lg border py-2.5 pl-9 pr-3 text-sm focus:border-transparent focus:ring-2',
                isDark
                  ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400'
                  : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500',
              )}
              style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
            />
          </div>
        ) : null}
        {contactTypes.length > 0 ? (
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={cn(
              'rounded-lg border px-3 py-2.5 text-sm sm:min-w-[8rem]',
              isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900',
            )}
          >
            <option value="all">All types</option>
            {contactTypes.map((t) => (
              <option key={t} value={t} className="capitalize">
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <BrandedLoadingSpinner mode="compact" size="sm" primaryColor={primaryColor} showSurface={false} showProgressBar={false} />
          <p className={cn('text-sm', isDark ? 'text-gray-400' : 'text-gray-500')}>Loading CRM contacts…</p>
        </div>
      ) : filteredSorted.length === 0 ? (
        <div className={cn('py-12 text-center', isDark ? 'text-gray-400' : 'text-gray-500')}>
          <Building2 className="mx-auto mb-3 h-12 w-12 opacity-50" />
          <p className="font-medium">No CRM contacts with phone numbers</p>
          <p className="mt-1 text-sm opacity-80">
            Add phone or mobile on CRM records, or adjust search filters.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {filteredSorted.map((c) => {
            const phone = primaryPhone(c);
            if (!phone) return null;
            const secondary =
              c.mobile?.trim() && c.phone?.trim() && c.mobile.trim() !== c.phone.trim()
                ? c.mobile.trim() === phone
                  ? c.phone.trim()
                  : c.mobile.trim()
                : null;
            const name = displayName(c);
            return (
              <li
                key={`${c.id}-${phone}`}
                className={cn(
                  'flex items-center justify-between gap-2 rounded-lg p-3',
                  isDark ? 'bg-gray-800' : 'bg-gray-50',
                )}
              >
                <div className="min-w-0 flex-1">
                  <p className={cn('truncate text-sm font-medium', isDark ? 'text-white' : 'text-gray-900')}>
                    {name}
                  </p>
                  <p className={cn('truncate font-mono text-xs', isDark ? 'text-gray-300' : 'text-gray-700')}>
                    {phone}
                    {secondary ? (
                      <span className={cn('font-sans', isDark ? 'text-gray-500' : 'text-gray-500')}>
                        {' '}
                        · {secondary}
                      </span>
                    ) : null}
                  </p>
                  {(c.company || c.job_title || c.contact_type) && (
                    <p className={cn('truncate text-xs', isDark ? 'text-gray-500' : 'text-gray-500')}>
                      {[c.job_title, c.company].filter(Boolean).join(' · ')}
                      {c.contact_type ? ` · ${c.contact_type}` : ''}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {onOpenContact && c.contact_id ? (
                    <button
                      type="button"
                      onClick={() => onOpenContact(c.contact_id as number)}
                      className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-transform active:scale-95',
                        isDark
                          ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
                      )}
                      title={`Open CRM record for ${name}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onSelectPhone(phone, name)}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-500 text-white transition-transform hover:bg-green-600 active:scale-95"
                    title={`Call ${name}`}
                  >
                    <Phone className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default CrmDialContactsList;
